import { Injectable, inject } from '@angular/core';
import { Configuration, FrontendApi, Session } from '@ory/client';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ENV, Environment } from '@aw/prypco/environment';

@Injectable({
  providedIn: 'root',
})
export class OryAuthService {
  // #region Injections
  private readonly environment = inject(ENV) as Environment;
  // #endregion

  // #region Constants
  private readonly basePath = this.environment.endpoints.oryAuth;
  // #endregion

  private oryClient = new FrontendApi(
    new Configuration({
      basePath: this.basePath,
      baseOptions: { withCredentials: true },
    }),
  );

  // #region Public Methods
  getSession(): Observable<Session | null> {
    return from(this.oryClient.toSession()).pipe(
      map((response) => response.data as Session),
      catchError(() => of(null)),
    );
  }

  startBrowserLogin(returnTo?: string): Observable<string> {
    return from(this.oryClient.createBrowserLoginFlow({ returnTo })).pipe(
      map((response) => response.data.id),
    );
  }

  startBrowserRegistration(returnTo?: string): Observable<string> {
    return from(
      this.oryClient.createBrowserRegistrationFlow({ returnTo }),
    ).pipe(map((response) => response.data.id));
  }

  getLoginFlow(flowId: string): Observable<any> {
    return from(this.oryClient.getLoginFlow({ id: flowId })).pipe(
      map((response) => response.data),
    );
  }

  getRegistrationFlow(flowId: string): Observable<any> {
    return from(this.oryClient.getRegistrationFlow({ id: flowId })).pipe(
      map((response) => response.data),
    );
  }

  updateLoginFlow(flowId: string, body: any): Observable<any> {
    return from(
      this.oryClient.updateLoginFlow({
        flow: flowId,
        updateLoginFlowBody: body,
      }),
    ).pipe(map((response) => response.data));
  }

  updateRegistrationFlow(flowId: string, body: any): Observable<any> {
    return from(
      this.oryClient.updateRegistrationFlow({
        flow: flowId,
        updateRegistrationFlowBody: body,
      }),
    ).pipe(map((response) => response.data));
  }

  logout(): Observable<void> {
    return from(this.oryClient.createBrowserLogoutFlow()).pipe(
      switchMap((response) =>
        from(
          this.oryClient.updateLogoutFlow({
            token: response.data.logout_token,
          }),
        ),
      ),
      map(() => undefined),
    );
  }
  // #endregion
}
